/**
 * TODO: components.input-edit
 * https://elsevier.atlassian.net/browse/OSMS-297
 * Reusable component to edit input, should be an Input with a Checkmark button and a Times or Cancel button.
 *
 * Used in: playlist-page.
 */
import React, {
  ChangeEvent,
  MouseEvent,
  useEffect,
  useState,
  KeyboardEvent,
} from 'react';
import { Box } from '@osmosis/react-ui/components/Box';
import { Flex } from '@osmosis/react-ui/components/Box';
import { IconButton } from '@osmosis/react-ui/components/IconButton';
import { Input } from '@osmosis/react-ui/components/Input';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { lighten, darken } from '@osmosis/shared/styles/colorModifiers';
import { useIntl } from 'react-intl';

type Props = {
  inputState: [string, React.Dispatch<React.SetStateAction<string>>];
  onConfirm: (e: MouseEvent<HTMLElement>) => Promise<void>;
  onCancel: (e: MouseEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  placeholder?: string;
  hideCancelBtn?: boolean;
  htmlFor?: string;
};

/**
 * InputEdit.
 */
const InputEdit = (props: Props) => {
  const {
    inputState,
    onConfirm,
    onCancel,
    autoFocus = false,
    placeholder,
    hideCancelBtn = false,
    htmlFor,
  } = props;

  const [isPlaylistErrorActive, setPlaylistErrorActive] = useState(false);
  const [inputValue, setInputValue] = inputState;
  const colors = useColors();
  const intl = useIntl();

  const handleConfirm = (e: MouseEvent<HTMLElement>) => {
    if (!isPlaylistErrorActive && !inputValue.length) {
      setPlaylistErrorActive(true);
      return;
    }

    onConfirm(e);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue) {
      handleConfirm(e as unknown as MouseEvent<HTMLElement>);
    }
  };

  useEffect(() => {
    if (isPlaylistErrorActive && inputValue.length)
      setPlaylistErrorActive(false);
  }, [inputValue]);

  return (
    <Box>
      <Flex alignItems="stretch" sx={{ gap: 2 }}>
        <label htmlFor={htmlFor}>
          <Input
            id="input-edit"
            onKeyDown={handleKeyDown}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            value={inputValue}
            sx={{
              outline: 'none',
              bg: isPlaylistErrorActive ? lighten(0.3, colors.red) : 'gray5',
              '::placeholder': {
                color: isPlaylistErrorActive
                  ? darken(0.2, colors.red)
                  : 'gray50',
              },
              borderRadius: '7px 7px 0 0',
              borderWidth: '0 0 2px',
              borderColor: isPlaylistErrorActive
                ? darken(0.1, colors.red)
                : 'primary',
            }}
            py={2}
            px={3}
            data-testid="edit-input"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setInputValue(e.target.value)
            }
            placeholder={placeholder}
          />
        </label>

        <IconButton
          type="button"
          bg={colors['positive']}
          color={colors['textInvert']}
          iconSize={4}
          disabled={!inputValue}
          icon={<Icon icon={faCheck} />}
          data-testid="edit-playlist-btn"
          flexShrink="0"
          onClick={handleConfirm}
          aria-label={intl.formatMessage({
            id: 'common.save',
            defaultMessage: 'Save',
          })}
        />

        {!hideCancelBtn && (
          <IconButton
            type="button"
            bg={colors['negative']}
            color={colors['textInvert']}
            iconSize={4}
            icon={<Icon icon={faXmark} />}
            onClick={(e: MouseEvent<HTMLElement>) => {
              onCancel(e);
            }}
            data-testid="close-playlist-btn"
            flexShrink="0"
            aria-label={intl.formatMessage({
              id: 'close-playlist-btn',
              defaultMessage: 'Cancel',
            })}
          />
        )}
      </Flex>
    </Box>
  );
};

export default InputEdit;
